<template>
  <div ref="imageContainer" :class="['single-image-zoom', 'image-magnifier', wrapperClass]">
    <div class="single-image-zoom__image-container">
      <div class="single-image-zoom__image-wrapper" :class="transparentBackgroundClasses">
        <img
          ref="imageElement"
          class="single-image-zoom__image js-magnify-image"
          :class="{
            'single-image-zoom__image--inline': isInline,
            'single-image-zoom__image--artwork': isArtwork,
            'single-image-zoom__image--full-width': isFullWidth
          }"
          :src="src"
          :alt="alt"
          v-bind="$attrs"
          @load="onImageLoad"
        />
      </div>
    </div>
    <!-- Magnifier elements for zoom functionality -->
    <div
      class="image-magnifier__preview js-magnify-preview"
      :data-background-color="computedImageBackground"
    />
    <div class="image-magnifier__lens js-magnify-lens" />
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";

import { isMobile, isTouchDevice } from "common/utils/browser";
import { BACKGROUND_TYPE, COLORS } from "components/jiffy/design-lab/shared/constants";
import ImageMagnifier from "components/jiffy/image-magnifier/image-magnifier";

import { useDtfStore } from "../store";
import { SINGLE_IMAGE_ZOOM } from "./constants";

export default {
  name: "SingleImageZoom",
  props: {
    src: {
      type: String,
      required: true
    },
    alt: {
      type: String,
      default: "Preview Image"
    },
    wrapperClass: {
      type: String,
      required: true
    },
    isModal: {
      type: Boolean,
      default: false
    },
    position: {
      type: String,
      default: "left"
    },
    isArtwork: {
      type: Boolean,
      default: false
    },
    isFullWidth: {
      type: Boolean,
      default: false
    },
    isInline: {
      type: Boolean,
      default: false
    },
    imageBackground: {
      type: String,
      default: "transparent"
    },
    overrideBackgroundType: {
      type: String,
      default: undefined
    }
  },
  emits: ["load", "canvasLoaded"],
  data() {
    return {
      imageHeight: "unset",
      magnifier: null,
      imageIntrinsicHeight: 0,
      imageIntrinsicWidth: 0
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "experiments",
      "isSticker",
      "isStickerSample",
      "isStandardArtwork",
      "backgroundType"
    ]),
    effectiveBackgroundType() {
      return this.overrideBackgroundType !== undefined
        ? this.overrideBackgroundType
        : this.backgroundType;
    },
    isTappable() {
      return isTouchDevice() || isMobile();
    },
    isTransparent() {
      return this.imageBackground === "transparent";
    },
    computedImageBackground() {
      if (this.effectiveBackgroundType === BACKGROUND_TYPE.GREEN) {
        return COLORS.GREEN_BACKGROUND;
      }
      return this.imageBackground;
    },
    transparentBackgroundClasses() {
      return {
        "single-image-zoom__image-wrapper--transparent-background":
          this.isTransparent && this.effectiveBackgroundType === BACKGROUND_TYPE.CHECKERED,
        "single-image-zoom__image-wrapper--standard-artwork":
          this.isStandardArtwork && this.effectiveBackgroundType === BACKGROUND_TYPE.CHECKERED
      };
    },
    imageMaxHeight() {
      // Keep ProColor/Spangle/Sticker bounded to avoid overlapping nearby UI
      if (this.isSticker || this.isStickerSample) return SINGLE_IMAGE_ZOOM.STICKER_MAX_HEIGHT;
      return this.isArtwork ? "unset" : SINGLE_IMAGE_ZOOM.DEFAULT_IMAGE_HEIGHT;
    },
    // Make constants available for v-bind in template
    constants() {
      return SINGLE_IMAGE_ZOOM;
    }
  },
  beforeUnmount() {
    // Clean up magnifier if it exists
    if (this.magnifier) {
      this.magnifier = null;
    }
  },
  methods: {
    ...mapActions(useDtfStore, ["setPreviewCanvasSize"]),
    onImageLoad(event) {
      const $image = event.target;

      // Mirror ImageCompare: if not artwork and the intrinsic height is small,
      // set a fixed displayed height on desktop to keep UI balanced
      if (
        !this.isArtwork &&
        $image &&
        $image.naturalHeight < SINGLE_IMAGE_ZOOM.NATURAL_HEIGHT_THRESHOLD
      ) {
        this.imageHeight = SINGLE_IMAGE_ZOOM.DEFAULT_IMAGE_HEIGHT;
      }

      // Trick to make sure the image is actually rendered on the screen.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.initializeImage($image);
        });
      });

      this.$emit("load", event);
    },
    initializeImage($image) {
      if ($image) {
        this.imageIntrinsicHeight = $image.naturalHeight;
        this.imageIntrinsicWidth = $image.naturalWidth;
      }

      if (!this.$refs.imageContainer) {
        return;
      }

      const { width, height } = this.$refs.imageContainer.getBoundingClientRect();
      this.setPreviewCanvasSize({ width, height });

      // Initialize magnifier after image is loaded and rendered
      // For SingleImageZoom, we always want magnifier on desktop (non-touch devices)
      if (!this.isTappable && !this.magnifier) {
        this.magnifier = new ImageMagnifier(this.wrapperClass, {
          isModal: this.isModal,
          position: this.position,
          useFixedPositioning: true
        });
        this.magnifier.initializeMagnifier();
      }

      this.$emit("canvasLoaded");
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/settings.scss";

.single-image-zoom {
  position: relative;
  overflow: visible; // allow magnifier preview to render outside the wrapper
  touch-action: none;
  max-width: 100%;

  @include with-tablet {
    width: fit-content;
  }

  @include with-desktop {
    height: auto;
  }

  &__image-container {
    width: 100%;
    height: 100%;
  }

  &__image-wrapper {
    height: 100%;
    background: v-bind(computedImageBackground);

    &--transparent-background {
      background: $transparent-bg;
    }

    &--standard-artwork {
      background-image: $transparent-bg;
      background-color: $white;
    }
  }

  &__image {
    display: block;
    max-width: 100%;
    max-height: v-bind(imageMaxHeight);

    &--full-width {
      width: v-bind("constants.FULL_WIDTH_IMAGE_WIDTH");
      max-width: 100%;
      max-height: fit-content;
    }

    &--artwork {
      max-width: 100%;
    }

    &--inline {
      max-height: v-bind("constants.INLINE_MAX_HEIGHT");
    }
  }
}
</style>
